'use client';

import { API_ROUTES } from '@shared/api-utils';
import { FormField } from '@shared/components/shadcn';
import { EditData } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { useMultistepForm } from '../../../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { TextInput } from '../../../../../../form-controls/TextInput/TextInput';
import { ContinueButton } from '../../../../../../Forms/Registration/components/ContinueButton/ContinueButton';
import { toast } from '../../../../../../Toast/Toast';
import { useVerificationApiMutation } from '../../../../hooks/useVerificationApiMutation';
import { isVerificationRateLimitError } from '../../../../utils/verificationErrorUtils';
import type { SendVerificationPayload, SendVerificationResponse } from '../../types';

type EmailStepProps = {
  originalEmail: string;
};

const EmailStep = ({ originalEmail }: EmailStepProps) => {
  const t = useTranslations('my_account');
  const tOtp = useTranslations('form.otp');
  const form = useFormContext<{ email: string; verificationId: string }>();
  const { goToNextStep, updateFormData } = useMultistepForm();

  const { mutateAsync: sendVerification, isPending } = useVerificationApiMutation<SendVerificationResponse, SendVerificationPayload>(API_ROUTES.MY_ACCOUNT.VERIFICATION.SEND);

  const watchedEmail = form.watch('email') ?? '';
  const isEmailChanged = watchedEmail.trim().toLowerCase() !== originalEmail.trim().toLowerCase();
  const isContinueDisabled = !isEmailChanged || isPending;
  const textInputHelpMessageMode = 'focusout' as const;

  const handleContinue = async () => {
    if (!isEmailChanged) {
      return;
    }

    const isValid = await form.trigger('email');
    if (!isValid) return;

    try {
      const response = await sendVerification({ method: 'email', value: form.getValues('email') });
      if (response) {
        form.setValue('verificationId', response.verificationId);
        updateFormData('verificationId', response.verificationId);
        goToNextStep();
      }
    } catch (error) {
      if (isVerificationRateLimitError(error)) {
        toast(tOtp('maxAttemptsReached'), { type: 'error' });
        return;
      }
      toast(t('pages.edit_email.email_step.error'), { type: 'error' });
    }
  };

  return (
    <div className="flex flex-col h-full" id="email-step" data-testid="email-step">
      <div className="md:my-6">
        <EditData size="extra-extra-large" className="mb-5" data-testid="edit-data-icon" />
        <h4 className="mb-2">{t('pages.edit_email.email_step.title')}</h4>
        <p className="text-foreground-subtle mb-8">{t('pages.edit_email.email_step.description')}</p>

        <div className="flex flex-col gap-4">
          <FormField
            name="email"
            render={({ field, fieldState }) => (
              <TextInput
                id="email"
                type="email"
                label={t('pages.edit_email.email_step.label')}
                placeholder={t('pages.edit_email.email_step.placeholder')}
                helpMessage={fieldState.error?.message}
                helpMessageMode={textInputHelpMessageMode}
                helpMessageType={fieldState.error ? 'error' : undefined}
                data-testid="email-input"
                inputTrackingProps={{
                  'data-track-name': 'Email',
                  'data-track-privacy': 'Mask',
                }}
                helperTrackingProps={{
                  'data-track-helper': 'Email',
                }}
                {...field}
              />
            )}
          />
        </div>
      </div>
      <div className="text-center mt-auto md:mt-8" data-track-container="Actions">
        <ContinueButton onClick={handleContinue} disabled={isContinueDisabled} loading={isPending} className="mt-auto" data-testid="continue-email-step" data-track-name="Continue">
          {t('pages.edit_email.email_step.continue')}
        </ContinueButton>
      </div>
    </div>
  );
};

export { EmailStep };
